import { useId, useState } from "react"

import { SelectorBuscable } from "@/componentes/ui/SelectorBuscable"
import {
  aTasaMicro,
  centimosDe,
  comoCampo,
  comoTasa,
  entreTasa,
  porTasa,
  type TasaMicro,
  tasaEntre,
  tasaRelativa,
} from "./conversion"

/** Una moneda del conversor con lo que vale HOY, en bolívares por una unidad. */
export type MonedaConversor = {
  codigo: string
  /** Bolívares por 1 unidad. El bolívar vale 1. Viene del servidor. */
  bsPorUnidad: string | null
}

type Props = {
  /** Las monedas entre las que se puede elegir. */
  monedas: MonedaConversor[]
  /** La moneda del importe que se está editando: la de la orden, o la de la cuenta. */
  principal: string
  /** Con cuál convertir al abrir. Si no sirve, se toma la primera posible. */
  referenciaInicial?: string | null
  /** El importe en `principal`, tal como está en su campo. */
  valor: string
  /** Devuelve el importe en `principal`, ya normalizado. */
  onCambiar: (valor: string) => void
  /**
   * El importe en la moneda de REFERENCIA, cuando quien usa el conversor lo guarda.
   *
   * ⚠ **Con esto el conversor deja de ser una calculadora y pasa a editar el dato que
   * se va a guardar.** En el pago, lo que sale del banco es un hecho del comprobante y
   * lo que se ajusta es cuánto de la orden cubre; la tasa del recuadro se DERIVA de
   * esos dos, que es la misma cuenta que hace el servidor al guardar
   * (`monto_debitado / monto_equivalente`). Sin esto, la tasa que se veía y la que
   * quedaba guardada podían ser dos números distintos.
   */
  equivalenteValor?: string
  onEquivalente?: (valor: string) => void
  etiqueta?: string
  /** Se pinta al pie. Sirve para decir qué se hace con esa tasa. */
  nota?: string
  /**
   * Falso cuando quien lo usa ya tiene su propia sección.
   *
   * ⚠ Un recuadro dentro de otro recuadro se lee como OTRO formulario, y en el pago
   * lo era: la conversión y los importes de la misma operación parecían dos cosas.
   */
  enmarcado?: boolean
}

/**
 * El mismo dinero en otra moneda, con UNA tasa y en los dos sentidos.
 *
 * ⚠ **Una sola tasa: la de la moneda que se elija.** Antes se enseñaban todas las
 * divisas con un campo de tasa cada una —cuatro cifras para decidir una, y dos sitios
 * donde teclear la misma—. Se elige contra qué moneda se convierte y se teclea su
 * valor; lo demás sobra.
 *
 * ⚠ **La tasa se lee como se habla y se MULTIPLICA**: «bolívares por 1 euro», y
 * 300 € × 974,4193 = 292.325,79 Bs. La etiqueta la escribe el par elegido, así que
 * desde una cuenta en dólares dice «USD por 1 EUR»: sigue siendo una multiplicación y
 * sigue habiendo un único número que teclear.
 *
 * ⚠ **Qué se mueve al teclear la tasa depende de quién sea el hecho.** Redactando —sin
 * `onEquivalente`— el hecho es la deuda, así que la tasa mueve el importe PRINCIPAL:
 * la orden son 300 € y «me lo cambiaron a 900». Pagando —con `onEquivalente`— el hecho
 * es lo que el banco debitó, que está en el comprobante, así que la tasa mueve lo que
 * ese pago CUBRE de la orden y el debitado no se toca.
 *
 * ⚠ **La sugerencia sale del BCV y la operación casi nunca es ésa**, por eso la tasa
 * se puede pisar. Dura lo que dure el formulario: desde aquí no se escribe en el
 * ledger de tasas (regla 20).
 *
 * ⚠ **Lo tecleado en la tasa NO se descarta al salir del campo.** Se descartaba, y el
 * número volvía solo al del BCV: quien corregía «me lo cambiaron a 927» veía
 * reaparecer 931,7503 y no había forma de dejar la tasa del comprobante. Sólo se
 * suelta al cambiar de moneda, porque entonces era la tasa de otra cosa.
 */
export function Conversor({
  monedas,
  principal,
  referenciaInicial,
  valor,
  onCambiar,
  equivalenteValor,
  onEquivalente,
  etiqueta = "Equivalencia",
  nota,
  enmarcado = true,
}: Props) {
  const id = useId()
  const opciones = monedas.filter((m) => m.codigo !== principal && m.bsPorUnidad !== null)
  const [referencia, setReferencia] = useState<string | null>(
    opciones.find((m) => m.codigo === referenciaInicial)?.codigo ?? opciones[0]?.codigo ?? null,
  )
  const [tasaTecleada, setTasaTecleada] = useState<string | null>(null)
  const [borrador, setBorrador] = useState<string | null>(null)

  const bsDe = (codigo: string | null): TasaMicro | null => {
    const moneda = monedas.find((m) => m.codigo === codigo)
    return moneda?.bsPorUnidad ? aTasaMicro(moneda.bsPorUnidad) : null
  }

  // ⚠ **La tasa se orienta sola, y siempre en la lectura natural.** Va «por 1» la
  // moneda que más vale, que es como se dice: «el dólar está a 848», nunca «el
  // bolívar está a 0,001179». No es sólo cómo se lee: con la unidad pequeña abajo,
  // seis decimales no bastan para volver sin perder céntimos.
  const bsReferencia = bsDe(referencia)
  const bsPrincipal = bsDe(principal)
  const referenciaAbajo =
    bsReferencia !== null && bsPrincipal !== null ? bsReferencia >= bsPrincipal : true
  const denominador = referenciaAbajo ? referencia : principal
  const numerador = referenciaAbajo ? principal : referencia
  const sugerida =
    bsReferencia !== null && bsPrincipal !== null
      ? referenciaAbajo
        ? tasaRelativa(bsReferencia, bsPrincipal)
        : tasaRelativa(bsPrincipal, bsReferencia)
      : null
  const centimos = centimosDe(valor)
  // ⚠ **Con `onEquivalente`, la tasa se DERIVA de los dos importes** en vez de salir
  // del BCV: es la cuenta que hace el servidor al guardar, así que lo que se lee aquí
  // es exactamente lo que va a quedar registrado con el pago.
  const controlado = onEquivalente !== undefined
  const equivalenteFijado = controlado ? centimosDe(equivalenteValor ?? "") : null
  const derivada =
    centimos !== null && equivalenteFijado !== null && equivalenteFijado !== 0n
      ? referenciaAbajo
        ? tasaEntre(centimos, equivalenteFijado)
        : tasaEntre(equivalenteFijado, centimos)
      : null
  const tasa = tasaTecleada !== null ? aTasaMicro(tasaTecleada) : (derivada ?? sugerida)

  const equivalente =
    equivalenteFijado ??
    (centimos === null || tasa === null
      ? null
      : referenciaAbajo
        ? entreTasa(centimos, tasa)
        : porTasa(centimos, tasa))

  /** El importe principal que corresponde a ese importe de referencia. */
  function principalDe(equivalenteCentimos: bigint, conTasa: TasaMicro): bigint {
    return referenciaAbajo
      ? porTasa(equivalenteCentimos, conTasa)
      : entreTasa(equivalenteCentimos, conTasa)
  }

  function escribirEquivalente(texto: string) {
    setBorrador(texto)
    if (onEquivalente) {
      // Pagando, este importe es un dato que se guarda: se manda tal cual y lo que
      // debitó el banco no se toca. La tasa que se ve pasa a ser la de esos dos.
      onEquivalente(texto)
      return
    }
    const tecleado = centimosDe(texto)
    if (tecleado === null || tasa === null) return
    onCambiar(comoCampo(principalDe(tecleado, tasa)))
  }

  function escribirTasa(texto: string) {
    setTasaTecleada(texto)
    const micro = aTasaMicro(texto)
    if (micro === null || micro === 0n) return
    if (onEquivalente) {
      // Lo que salió del banco es un hecho del comprobante: lo que se mueve al
      // corregir la tasa es cuánto de la orden cubre ese pago.
      if (centimos === null) return
      onEquivalente(
        comoCampo(referenciaAbajo ? entreTasa(centimos, micro) : porTasa(centimos, micro)),
      )
      return
    }
    if (equivalente !== null) onCambiar(comoCampo(principalDe(equivalente, micro)))
  }

  if (referencia === null) return null

  const campos = (
    <>
      <div className="grid gap-4 sm:grid-cols-3">
        <label className="block space-y-1 text-sm" htmlFor={`${id}-moneda`}>
          <span className="font-medium">Moneda</span>
          {/* El selector trabaja con números, así que la opción es la POSICIÓN en
              la lista y el código es sólo su etiqueta. */}
          <SelectorBuscable
            id={`${id}-moneda`}
            opciones={opciones.map((m, i) => ({ valor: i, etiqueta: m.codigo }))}
            valor={opciones.findIndex((m) => m.codigo === referencia)}
            onCambiar={(indice) => {
              setReferencia(indice === null ? null : (opciones[indice]?.codigo ?? null))
              // La tasa tecleada era la de OTRA moneda: vuelve la sugerida.
              setTasaTecleada(null)
            }}
          />
        </label>
        <label className="block space-y-1 text-sm" htmlFor={`${id}-tasa`}>
          <span className="font-medium">
            Tasa · {numerador} por 1 {denominador}
          </span>
          <input
            id={`${id}-tasa`}
            value={tasaTecleada ?? (tasa === null ? "" : comoTasa(tasa))}
            onChange={(e) => escribirTasa(e.target.value)}
            inputMode="decimal"
            className="campo w-full tabular-nums"
          />
        </label>
        <label className="block space-y-1 text-sm" htmlFor={`${id}-importe`}>
          <span className="font-medium">Importe en {referencia}</span>
          <input
            id={`${id}-importe`}
            value={
              onEquivalente
                ? (equivalenteValor ?? "")
                : (borrador ?? (equivalente === null ? "" : comoCampo(equivalente)))
            }
            onChange={(e) => escribirEquivalente(e.target.value)}
            onBlur={() => setBorrador(null)}
            inputMode="decimal"
            className="campo w-full tabular-nums"
          />
        </label>
      </div>
      <p className="text-xs" style={{ color: "var(--fg-muted)" }}>
        {nota ?? "Teclea el importe o la tasa: lo otro se recalcula."}
      </p>
    </>
  )

  if (!enmarcado) return campos

  return (
    <fieldset className="tarjeta space-y-3 p-3">
      <legend className="px-1 text-sm font-medium">{etiqueta}</legend>
      {campos}
    </fieldset>
  )
}
